"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
/**
 * @ignore
 */
function usePrefersReducedMotion() {
    const [reduce, setReduce] = React.useState(false);
    React.useEffect(() => {
        const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        const update = () => setReduce(!!mq?.matches);
        update();
        mq?.addEventListener?.('change', update);
        return () => mq?.removeEventListener?.('change', update);
    }, []);
    return reduce;
}
/**
 * A custom React hook for creating a collapsible section with smooth height animation.
 *
 * Supports both uncontrolled and controlled usage:
 * - Uncontrolled: use `defaultOpen` and call `toggle()`.
 * - Controlled: use `open` and handle `onOpenChange` to set the new value.
 *
 * @param {boolean} defaultOpen - Initial state when uncontrolled. Default is `false`.
 * @param {number} durationMs - Animation duration in ms. Default is `200`.
 * @param {Function} onToggleDone - Callback after the animation completes with the new open state.
 * @param {boolean} open - Controlled open state (when provided).
 * @param {Function} onOpenChange - Called when a toggle is requested (controlled mode).
 */
function useCollapsible({ defaultOpen = false, durationMs = 200, onToggleDone, open, onOpenChange, } = {}) {
    const prefersReduced = usePrefersReducedMotion();
    const actualDuration = prefersReduced ? 0 : durationMs;
    const initialOpenRef = React.useRef(open !== undefined ? !!open : !!defaultOpen);
    const isControlled = open !== undefined;
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpenRef.current);
    const resolvedOpen = isControlled ? !!open : uncontrolledOpen;
    const [isAnimated, setIsAnimated] = React.useState(false);
    const [isContentHidden, setIsContentHidden] = React.useState(!initialOpenRef.current);
    const [maxHeight, setMaxHeight] = React.useState(initialOpenRef.current ? undefined : 0);
    const innerRef = React.useRef(null);
    const timeoutRef = React.useRef(null);
    const isFirstRender = React.useRef(true);
    const clearTimer = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };
    const runOpenAnimation = React.useCallback(() => {
        clearTimer();
        setIsAnimated(!prefersReduced);
        setIsContentHidden(false);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const el = innerRef.current;
                if (el)
                    setMaxHeight(el.scrollHeight);
            });
        });
        timeoutRef.current = setTimeout(() => {
            setIsAnimated(false);
            onToggleDone?.(true);
            timeoutRef.current = null;
        }, actualDuration);
    }, [actualDuration, onToggleDone, prefersReduced]);
    const runCloseAnimation = React.useCallback(() => {
        clearTimer();
        const el = innerRef.current;
        setIsAnimated(!prefersReduced);
        if (el) {
            setMaxHeight(el.scrollHeight);
            requestAnimationFrame(() => setMaxHeight(0));
        }
        else {
            setMaxHeight(0);
        }
        timeoutRef.current = setTimeout(() => {
            setIsAnimated(false);
            setIsContentHidden(true);
            onToggleDone?.(false);
            timeoutRef.current = null;
        }, actualDuration);
    }, [actualDuration, onToggleDone, prefersReduced]);
    React.useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (resolvedOpen)
            runOpenAnimation();
        else
            runCloseAnimation();
    }, [resolvedOpen, runOpenAnimation, runCloseAnimation]);
    React.useEffect(() => {
        const element = innerRef.current;
        if (!element)
            return;
        const updateHeight = () => {
            if (resolvedOpen && innerRef.current) {
                setMaxHeight(innerRef.current.clientHeight);
            }
        };
        let ro = null;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(updateHeight);
            ro.observe(element);
        }
        else {
            window.addEventListener('resize', updateHeight);
        }
        return () => {
            if (ro)
                ro.disconnect();
            else
                window.removeEventListener('resize', updateHeight);
        };
    }, [resolvedOpen]);
    // Cleanup timeout on unmount
    React.useEffect(() => {
        return () => clearTimer();
    }, []);
    const toggle = React.useCallback(() => {
        if (isControlled) {
            onOpenChange?.(!resolvedOpen);
        }
        else {
            setUncontrolledOpen((prev) => !prev);
            onOpenChange?.(!uncontrolledOpen);
        }
    }, [isControlled, onOpenChange, resolvedOpen, uncontrolledOpen]);
    const containerStyle = React.useMemo(() => ({
        overflow: 'hidden',
        transition: isAnimated ? `max-height ${durationMs}ms ease` : 'none',
        maxHeight: maxHeight !== undefined ? `${maxHeight}px` : 'none',
    }), [durationMs, isAnimated, maxHeight]);
    const getContainerProps = React.useCallback((props = {}) => {
        const { style, ...rest } = props;
        return {
            ...rest,
            'aria-hidden': isContentHidden,
            style: {
                ...containerStyle,
                ...style,
            },
        };
    }, [containerStyle, isContentHidden]);
    return {
        isCollapseOpen: resolvedOpen,
        isAnimated,
        isContentHidden,
        maxHeight,
        toggle,
        innerRef,
        getContainerProps,
    };
}
exports.default = useCollapsible;
//# sourceMappingURL=useCollapsible.js.map